import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback } from 'react';
import Raven from 'raven-js';
import styled from 'styled-components';
import I18n from 'I18n';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UILink from 'UIComponents/link/UILink';
import { FormattedMessage } from 'LearningCenterUI/components/I18n';
import { CALYPSO, OLAF } from 'HubStyleTokens/colors';
import { GRID_BREAKPOINT_MEDIUM } from 'HubStyleTokens/sizes';
import { CertificateImage as PrivateCertificateImage } from '../../../features/achievementImages';
import { addUrlParams } from 'LearningCenterUI/utils/RoutingUtils';
import { useAchievementTracks, usePublicAchievement, getMetadataLanguageFallback } from '../../../features/achievement';
import ImageLoader from '../../../components/ImageLoader';
const CertImageWrapper = styled.div.withConfig({
  displayName: "CertificateImage__CertImageWrapper",
  componentId: "sc-17w27zr-0"
})(["position:relative;box-shadow:1px 2px 10px 1px rgb(0 0 0 / 20%);border-radius:10px;background:", ";margin-top:-30px;overflow:hidden;padding-top:", "%;&::after{content:'';position:absolute;top:0;right:0;bottom:0;left:0;pointer-events:none;opacity:0.1;transition-property:box-shadow,background-color;transition-duration:200ms;}&:hover::after{box-shadow:0 0 9px 1px ", ";background:", ";}a{position:absolute;top:0;right:0;bottom:0;left:0;}img{position:absolute;top:0;right:0;bottom:0;left:0;}@media (min-width:", "){margin-bottom:-30px;}"], OLAF, 2308 / 3046 * 100, CALYPSO, CALYPSO, GRID_BREAKPOINT_MEDIUM);
const ImgLoadingError = styled.div.withConfig({
  displayName: "CertificateImage__ImgLoadingError",
  componentId: "sc-17w27zr-1"
})(["position:absolute;top:50%;width:100%;text-align:center;"]);
const NoCertError = styled(UIErrorMessage).withConfig({
  displayName: "CertificateImage__NoCertError",
  componentId: "sc-17w27zr-2"
})(["position:absolute;width:100%;height:100%;top:0;&& img{position:relative;margin-top:1rem;width:33%;}"]);

const addCtaParam = (href, cta) => addUrlParams(href, {
  'hubs_content-cta': cta
});

const BrochureLinkWrapper = ({
  brochureUrl,
  children,
  trackEvent
}) => {
  const trackClickThrough = useCallback(({
    currentTarget
  }) => trackEvent({
    action: 'click-primary-cert',
    href: currentTarget && currentTarget.href,
    isExternalLink: true
  }), [trackEvent]);

  if (!brochureUrl) {
    return children;
  }

  return /*#__PURE__*/_jsx(UILink, {
    "aria-label": I18n.text('academyPublic.certification.seeMoreOnThisCourse'),
    "data-test-id": "achievement-cert-image",
    external: true,
    href: addCtaParam(brochureUrl, 'achievement-cta-cert-image'),
    iconProps: {
      className: 'hidden'
    },
    onClick: trackClickThrough,
    target: "_blank",
    children: /*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyPublic.certification.seeMoreOnThisCourse"
      }),
      children: children
    })
  });
};

const handleImageError = ({
  currentTarget
}) => {
  Raven.captureMessage(`Achievements: error loading cert image`, {
    extra: {
      src: currentTarget.src
    }
  });
};

const CertificateImage = ({
  inModal,
  trackEvent,
  publicId,
  language
}) => {
  const {
    publicAchievement
  } = usePublicAchievement(publicId);
  const tracks = useAchievementTracks(publicId);
  const track = tracks ? tracks[0] : {
    id: null,
    award: {
      certificate: null
    }
  };
  const {
    id: trackId,
    award
  } = track;
  const src = award ? award.certificate : '';
  const {
    brochureUrl
  } = tracks ? getMetadataLanguageFallback(tracks[0].metadata, language) : {
    brochureUrl: null
  };
  const maybeTrackedSrc = !inModal && src && !src.match(/^data:/) ? addUrlParams(src, {
    onAchievementsPage: true
  }) : src;

  if (!publicAchievement || !maybeTrackedSrc && !trackId) {
    return /*#__PURE__*/_jsx(CertImageWrapper, {
      "data-test-id": "cert-image-wrapper",
      children: /*#__PURE__*/_jsx(NoCertError, {
        title: I18n.text('academyPublic.certification.errors.noCertSrcTitle'),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyPublic.certification.errors.noCertSrc"
        })
      })
    });
  }

  return /*#__PURE__*/_jsx(CertImageWrapper, {
    "data-test-id": "cert-image-wrapper",
    children: /*#__PURE__*/_jsx(BrochureLinkWrapper, {
      brochureUrl: brochureUrl,
      trackEvent: trackEvent,
      children: /*#__PURE__*/_jsx(ImageLoader, Object.assign({}, publicAchievement.visibilityType === 'PRIVATE' ? {
        trackId,
        component: PrivateCertificateImage,
        key: trackId
      } : {
        src: maybeTrackedSrc,
        key: maybeTrackedSrc
      }, {
        alt: I18n.text('academyPublic.certification.altText'),
        errorComponent: /*#__PURE__*/_jsx(ImgLoadingError, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyPublic.certification.errors.imgLoadingError"
          })
        }),
        onError: handleImageError
      }))
    })
  });
};

export default CertificateImage;